import {
  P2GBox,
  P2GButton,
  P2GContainer,
  P2GImage,
  P2GText,
  useP2GMediaQueryOnly,
  useP2GMediaQueryUp,
  useTheme
} from '@parcel2go/core-ui';
import React, { useCallback } from 'react';
import ChatButton from '~/apps/smartsend/components/ChatButton';
import { EventSections } from '~/apps/smartsend/lib/events/EventLogger';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';

import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import { useExtendedDetails } from '~/hooks/use-extended-details';

interface VipBannerProps {
  isWidget?: boolean;
  onChatClick?: () => void;
  wrapper: (children: React.ReactNode) => React.ReactNode;
}

const colors: Record<string, string> = {
  BannerBorder: '#27CE5A',
  OutlineBtn: '#26A44C',
  backgroundWhite: 'rgba(255,255,255,0.6)',
  backgroundGreen: 'rgba(39, 206, 90, 0.2)'
};

const VipDetails = (props: VipBannerProps) => {
  const { track } = useSmartSendAnalytics();
  const { details } = useExtendedDetails();
  const { isWidget = false, onChatClick } = props;
  const isXs = useP2GMediaQueryOnly('xs');
  const isLg = useP2GMediaQueryUp('lg');
  const theme = useTheme();
  const isCompact = isXs || isWidget;

  const logEvent = useCallback(
    (link: string) => {
      track({
        type: 'smartsend_action_menu_click',
        payload: {
          ...GenerateBaseAnalyticsPayload([EventSections.MyAccount, EventSections.QuickActions]),
          link: link
        }
      });
    },
    [track]
  );

  if (details?.metadata?.supportGroup !== 'VIP') {
    return null;
  }

  const contactMethods = [
    details.metadata.supportEmail && { text: 'Email', value: details.metadata.supportEmail, linkTag: 'mailto' },
    details.metadata.supportPhone && { text: 'call', value: details.metadata.supportPhone, linkTag: 'tel' }
  ].filter(Boolean);

  const BaseBtnStyles = {
    border: `2px solid ${colors.OutlineBtn}`,
    color: colors.OutlineBtn,
    '&:hover': {
      background: colors.backgroundWhite,
      border: `2px solid ${colors.OutlineBtn}`,
      color: colors.OutlineBtn
    },
    fontSize: {
      xs: theme.typography.pxToRem(14),
      sm: theme.typography.pxToRem(16)
    },
    padding: {
      xs: theme.spacing(0.75, 1),
      sm: theme.spacing(0.75, 2)
    },
    '& span svg path': {
      fill: 'none'
    },
    '&:hover span svg path': {
      fill: 'none'
    }
  };

  return props.wrapper(
    <P2GBox
      maxWidth={isWidget ? theme.breakpoints.values.sm : '100%'}
      mx="auto"
      borderRadius={isWidget ? theme.spacing(2) : '0px'}
      backgroundSize="cover, auto 600px"
      backgroundBlendMode="overlay"
      padding={isXs ? theme.spacing(2, 0) : theme.spacing(2)}
      sx={{
        borderBlock: `3px solid #CDCDCD`,
        borderInline: isWidget ? `3px solid #CDCDCD` : 'none',
        background: `url('https://images.ctfassets.net/v78wipeni189/1J4spLVM0HRZLB8aTdsshp/145495073029efceceaf499ff76d426d/Background.svg') left center no-repeat`
      }}
    >
      <P2GContainer maxWidth="lg">
        <P2GBox
          display="flex"
          flexWrap={isLg && !isWidget ? 'nowrap' : 'wrap'}
          flexDirection={isWidget ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems={isCompact ? 'stretch' : 'center'}
          gap={theme.spacing(2)}
        >
          <P2GBox
            display="flex"
            flexDirection={isCompact ? 'column' : 'row'}
            justifyContent="flex-start"
            alignItems={isCompact ? 'start' : 'center'}
            gap={isCompact ? theme.spacing(0.5) : theme.spacing(2)}
          >
            <P2GImage
              src="https://images.ctfassets.net/v78wipeni189/4vt8Z67tPB5qmH3S6jYX6k/9ed1c9e225de28fe765ec99e534b4f78/p2g-vip-logo.svg"
              alt="P2G VIP Logo"
              style={{
                width: isCompact ? 130 : 150
              }}
            />
            <P2GText variant="body2">
              Need help?{' '}
              {contactMethods.map((method, index) => (
                <React.Fragment key={method.text}>
                  {method.text}{' '}
                  <a
                    style={{ textDecoration: 'none', color: theme.palette.common.black }}
                    href={method.linkTag ? `${method.linkTag}:${method.value}` : undefined}
                  >
                    <strong>{method.value}</strong>
                  </a>
                  {index < contactMethods.length - 1 && ', '}
                </React.Fragment>
              ))}
              {contactMethods.length > 0 && ' or '}
              speak to us via VIP priority chat.
            </P2GText>
          </P2GBox>
          <P2GBox
            width={isCompact ? '100%' : 'auto'}
            display="flex"
            justifyContent="space-between"
            flexDirection={isWidget ? 'column' : 'row'}
            gap={theme.spacing(1.25)}
            sx={{
              '& > *': {
                flex: 1
              }
            }}
          >
            {details.metadata.supportEmail && (
              <P2GButton
                variant="outlined"
                href={`mailto:${details.metadata.supportEmail}`}
                fullWidth={isCompact}
                startIcon={<MailIcon />}
                sx={BaseBtnStyles}
              >
                Email VIP Team
              </P2GButton>
            )}
            <ChatButton
              onOpenChat={() => logEvent('LiveChat')}
              customButton={
                <P2GButton
                  variant="outlined"
                  onClick={onChatClick}
                  fullWidth={isCompact}
                  startIcon={<ChatIcon />}
                  sx={BaseBtnStyles}
                >
                  Live VIP Chat
                </P2GButton>
              }
            />
          </P2GBox>
        </P2GBox>
      </P2GContainer>
    </P2GBox>
  );
};

export default VipDetails;

const ChatIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4.65237 11.7785C5.77637 12.3551 7.06935 12.5113 8.29831 12.2189C9.52727 11.9265 10.6114 11.2048 11.3553 10.1838C12.0992 9.16281 12.454 7.9097 12.3558 6.65026C12.2575 5.39083 11.7127 4.20789 10.8194 3.31463C9.92617 2.42137 8.74324 1.87652 7.4838 1.77827C6.22437 1.68002 4.97125 2.03482 3.95027 2.77875C2.92928 3.52267 2.20758 4.60679 1.91519 5.83576C1.62281 7.06472 1.77899 8.3577 2.35558 9.4817L1.17773 12.9563L4.65237 11.7785Z"
      stroke={colors.OutlineBtn}
      strokeWidth="1.17784"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MailIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.7783 2.3562H2.35558C1.70507 2.3562 1.17773 2.88354 1.17773 3.53404V10.6011C1.17773 11.2516 1.70507 11.7789 2.35558 11.7789H11.7783C12.4288 11.7789 12.9562 11.2516 12.9562 10.6011V3.53404C12.9562 2.88354 12.4288 2.3562 11.7783 2.3562Z"
      stroke={colors.OutlineBtn}
      strokeWidth="1.17784"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.9562 4.12292L7.67353 7.47977C7.49172 7.59369 7.2815 7.6541 7.06694 7.6541C6.85239 7.6541 6.64217 7.59369 6.46035 7.47977L1.17773 4.12292"
      stroke={colors.OutlineBtn}
      strokeWidth="1.17784"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
